import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar, Download, Music, Video, Clock, MapPin, Users, X, FileText, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import PageHeader from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { downloadICSFile } from '@/utils/icsDownload';
import { buildICSFromSchedules, buildNativeCalendarEventsFromSchedules } from '@/utils/icsBuilder';
import { getWorshipTeamMemberByUser, getMediaTeamMemberByUser, getReceptionTeamMemberByUser, getKidsTeamMemberByUser } from '@/lib/teamMemberLookup';
import { jsPDF } from 'jspdf';
import { importEventsToNativeCalendar, isNativeIOS, openBlobFileOnNative } from '@/utils/nativeAndroidFiles';

const scheduleNotificationTypeByTeam = {
  worship: 'worship_schedule',
  media: 'media_schedule',
  reception: 'reception_schedule',
  kids: 'kids_schedule',
};

const scheduleTeamLabel = {
  worship: 'Equipe de Louvor',
  media: 'Equipe de Mídia',
  reception: 'Equipe de Recepcao',
  kids: 'Kids MBV',
};

const scheduleTeamTag = {
  worship: 'LOUVOR',
  media: 'MIDIA',
  reception: 'RECEPCAO',
  kids: 'KIDS',
};

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const normalizeMemberName = (value) => normalizeText(value).replace(/\s+/g, ' ');

export default function CalendarioIntegrado() {
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [exporting, setExporting] = useState(false);
  const nativeIOS = isNativeIOS();
  const parseScheduleDate = (dateStr) => {
    if (!dateStr) return null;
    const normalized = String(dateStr).slice(0, 10);
    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return new Date(dateStr);
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  // Fetch current user
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: permissions } = useQuery({
    queryKey: ['adminPermissions', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const byUserEmail = await base44.entities.AdminPermission.filter({ user_email: user.email });
      if (byUserEmail.length > 0) return byUserEmail[0];
      const byEmail = await base44.entities.AdminPermission.filter({ email: user.email });
      return byEmail[0] || null;
    },
    enabled: !!user?.email
  });

  // Check user access - worship, media or kids member
  const { data: worshipMember } = useQuery({
    queryKey: ['currentWorshipMember', user?.id, user?.email],
    queryFn: () => getWorshipTeamMemberByUser(user),
    enabled: !!user
  });

  const { data: mediaMember } = useQuery({
    queryKey: ['currentMediaMember', user?.id, user?.email],
    queryFn: () => getMediaTeamMemberByUser(user),
    enabled: !!user
  });

  const { data: receptionMember } = useQuery({
    queryKey: ['currentReceptionMember', user?.id, user?.email],
    queryFn: () => getReceptionTeamMemberByUser(user),
    enabled: !!user
  });

  const { data: kidsMember } = useQuery({
    queryKey: ['currentKidsMember', user?.id, user?.email],
    queryFn: () => getKidsTeamMemberByUser(user),
    enabled: !!user
  });

  const isAdmin = user?.role === 'admin' || permissions?.is_super_admin;
  const hasAccess = isAdmin || worshipMember || mediaMember || receptionMember || kidsMember;

  // Fetch schedules
  const { data: worshipSchedules = [], isLoading: loadingWorship } = useQuery({
    queryKey: ['worshipSchedules'],
    queryFn: () => base44.entities.WorshipSchedule.list('-date', 100),
  });

  const { data: mediaSchedules = [], isLoading: loadingMedia } = useQuery({
    queryKey: ['mediaSchedules'],
    queryFn: () => base44.entities.MediaSchedule.list('-date', 100),
  });

  const { data: receptionSchedules = [], isLoading: loadingReception } = useQuery({
    queryKey: ['receptionSchedules'],
    queryFn: () => base44.entities.ReceptionSchedule.list('-date', 100),
  });

  const { data: kidsSchedules = [], isLoading: loadingKids } = useQuery({
    queryKey: ['kidsSchedules'],
    queryFn: () => base44.entities.KidsMBVSchedule.list('-date', 100),
  });

  const { data: notifications = [] } = useQuery({
    queryKey: ['allNotificationsForSchedules'],
    queryFn: () => base44.entities.Notification.list('-created_date', 2000),
  });

  const isLoading = loadingWorship || loadingMedia || loadingReception || loadingKids || !user;

  // Combine schedules with type identifier
  const allSchedules = useMemo(() => {
    const worship = worshipSchedules.map(s => ({ ...s, type: 'worship' }));
    const media = mediaSchedules.map(s => ({ ...s, type: 'media' }));
    const reception = receptionSchedules.map(s => ({ ...s, type: 'reception' }));
    const kids = kidsSchedules.map(s => ({ ...s, type: 'kids' }));
    return [...worship, ...media, ...reception, ...kids].sort((a, b) => parseScheduleDate(a.date) - parseScheduleDate(b.date));
  }, [worshipSchedules, mediaSchedules, receptionSchedules, kidsSchedules]);

  // Navigation functions
  const navigateMonth = (direction) => {
    setCurrentDate(direction === 'next' ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
  };

  const navigateWeek = (direction) => {
    setCurrentDate(direction === 'next' ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
  };

  const navigateDay = (direction) => {
    setCurrentDate(direction === 'next' ? addDays(currentDate, 1) : subDays(currentDate, 1));
  };

  const navigate = (direction) => {
    if (viewMode === 'month') navigateMonth(direction);
    else if (viewMode === 'week') navigateWeek(direction);
    else navigateDay(direction);
  };

  // Get days to display based on view mode
  const displayDays = useMemo(() => {
    if (viewMode === 'month') {
      const start = startOfWeek(startOfMonth(currentDate), { locale: ptBR });
      const end = endOfWeek(endOfMonth(currentDate), { locale: ptBR });
      return eachDayOfInterval({ start, end });
    } else if (viewMode === 'week') {
      const start = startOfWeek(currentDate, { locale: ptBR });
      const end = endOfWeek(currentDate, { locale: ptBR });
      return eachDayOfInterval({ start, end });
    } else {
      return [currentDate];
    }
  }, [currentDate, viewMode]);

  // Get schedules for a specific day
  const getSchedulesForDay = (day) => {
    return allSchedules.filter(s => isSameDay(parseScheduleDate(s.date), day));
  };

  const visibleSchedules = useMemo(() => {
    return allSchedules.filter((schedule) =>
      displayDays.some((day) => isSameDay(parseScheduleDate(schedule.date), day))
    );
  }, [allSchedules, displayDays]);

  const isScheduledForCurrentUser = (schedule) => {
    if (isAdmin) return true;

    const scheduleMembers = Array.isArray(schedule.members) ? schedule.members : [];
    const currentUserEmail = (user?.email || '').trim().toLowerCase();

    const hasEmailMatch = scheduleMembers.some((member) => {
      const memberEmail = (member?.member_email || member?.email || '').trim().toLowerCase();
      return memberEmail && memberEmail === currentUserEmail;
    });

    if (hasEmailMatch) return true;

    if (schedule.type === 'worship' && worshipMember?.id) {
      return scheduleMembers.some((member) => member?.id === worshipMember.id);
    }

    if (schedule.type === 'media' && mediaMember?.id) {
      return scheduleMembers.some((member) => member?.id === mediaMember.id);
    }

    if (schedule.type === 'reception' && receptionMember?.id) {
      return scheduleMembers.some((member) => member?.id === receptionMember.id);
    }

    if (schedule.type === 'kids' && kidsMember?.id) {
      return scheduleMembers.some((member) => member?.id === kidsMember.id);
    }

    return false;
  };

  const exportableSchedules = useMemo(() => {
    if (isAdmin) return visibleSchedules;
    return visibleSchedules.filter(isScheduledForCurrentUser);
  }, [visibleSchedules, isAdmin, worshipMember?.id, mediaMember?.id, receptionMember?.id, kidsMember?.id, user?.email]);

  const scheduleChangeRequests = useMemo(() => {
    const bySchedule = new Map();

    for (const notification of notifications || []) {
      const type = notification?.type;
      const scheduleId = notification?.reference_id;
      if (!type || !scheduleId) continue;
      if (!Object.values(scheduleNotificationTypeByTeam).includes(type)) continue;

      const title = normalizeText(notification?.title);
      const message = normalizeText(notification?.message);
      const isChangeRequest =
        title.includes('solicitacao de alteracao') ||
        message.includes('solicitou alteracao');

      if (!isChangeRequest) continue;

      const rawMessage = String(notification?.message || '');
      const memberMatch = rawMessage.match(/^(.+?)\s+solicitou altera(?:c|ç)[aã]o/i);
      const requestedBy = normalizeMemberName(memberMatch?.[1]);
      if (!requestedBy) continue;

      const key = `${type}:${scheduleId}`;
      if (!bySchedule.has(key)) {
        bySchedule.set(key, new Set());
      }
      bySchedule.get(key).add(requestedBy);
    }

    return bySchedule;
  }, [notifications]);

  const getMemberPresenceStatus = (schedule, member) => {
    const notificationType = scheduleNotificationTypeByTeam[schedule?.type];
    const scheduleKey = `${notificationType}:${schedule?.id}`;
    const requestedMembers = scheduleChangeRequests.get(scheduleKey);
    const requestedChange = requestedMembers?.has(normalizeMemberName(member?.name)) || false;
    const confirmed = !!member?.confirmed;

    if (requestedChange) return 'Solicitou troca';
    if (confirmed) return 'Confirmou presenca';
    return 'Nao confirmou';
  };

  const buildPdfForSchedules = (schedules, periodLabel) => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const usableWidth = pageWidth - margin * 2;
    let y = margin;

    const ensureSpace = (needed = 20) => {
      if (y + needed <= pageHeight - margin) return;
      doc.addPage();
      y = margin;
    };

    const addText = (text, options = {}) => {
      const {
        size = 10,
        bold = false,
        color = [31, 41, 55],
        gap = 14,
        indent = 0,
      } = options;

      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(size);
      doc.setTextColor(color[0], color[1], color[2]);
      const lines = doc.splitTextToSize(String(text || ''), usableWidth - indent);
      ensureSpace(lines.length * gap + 4);
      doc.text(lines, margin + indent, y);
      y += lines.length * gap;
    };

    addText('Relatorio de Escalas - Calendario Integrado', { size: 14, bold: true, gap: 18, color: [17, 24, 39] });
    addText(`Periodo: ${periodLabel}`, { size: 10, color: [75, 85, 99] });
    addText(`Gerado em: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, { size: 10, color: [75, 85, 99] });
    y += 4;

    if (!schedules.length) {
      addText('Nenhuma escala encontrada para o periodo selecionado.', { size: 11, color: [107, 114, 128] });
      return doc;
    }

    const sortedSchedules = [...schedules].sort((a, b) => parseScheduleDate(a.date) - parseScheduleDate(b.date));

    for (const schedule of sortedSchedules) {
      ensureSpace(40);
      y += 6;
      doc.setDrawColor(229, 231, 235);
      doc.line(margin, y, pageWidth - margin, y);
      y += 16;

      const teamTag = scheduleTeamTag[schedule.type] || 'EQUIPE';
      addText(`[${teamTag}] ${format(parseScheduleDate(schedule.date), 'dd/MM/yyyy')} - ${schedule.title || 'Escala'}`, {
        size: 12,
        bold: true,
        color: [17, 24, 39],
        gap: 16,
      });

      const baseInfo = [];
      baseInfo.push(scheduleTeamLabel[schedule.type] || 'Equipe');
      if (schedule.start_time) {
        baseInfo.push(
          schedule.end_time
            ? `Horario: ${schedule.start_time} - ${schedule.end_time}`
            : `Horario: ${schedule.start_time}`
        );
      }
      if (schedule.location) baseInfo.push(`Local: ${schedule.location}`);
      addText(baseInfo.join(' | '), { size: 10, color: [55, 65, 81], gap: 14 });

      const members = Array.isArray(schedule.members) ? schedule.members : [];
      const statusRows = members.map((member) => ({
        name: member?.name || 'Integrante',
        role: member?.role || '',
        status: getMemberPresenceStatus(schedule, member),
      }));

      const confirmedCount = statusRows.filter((m) => m.status === 'Confirmou presenca').length;
      const changeRequestCount = statusRows.filter((m) => m.status === 'Solicitou troca').length;
      const notConfirmedCount = statusRows.filter((m) => m.status === 'Nao confirmou').length;

      addText(
        `Resumo: Confirmou presenca ${confirmedCount} | Solicitou troca ${changeRequestCount} | Nao confirmou ${notConfirmedCount}`,
        { size: 10, bold: true, color: [31, 41, 55], gap: 14 }
      );

      if (!statusRows.length) {
        addText('Sem integrantes nessa escala.', { size: 10, color: [107, 114, 128], gap: 14, indent: 10 });
        continue;
      }

      for (const row of statusRows) {
        const roleLabel = row.role ? ` (${row.role})` : '';
        addText(`- ${row.name}${roleLabel}: ${row.status}`, { size: 10, color: [55, 65, 81], gap: 14, indent: 10 });
      }
    }

    return doc;
  };

  const periodLabel = useMemo(() => {
    if (!displayDays?.length) return '-';
    const start = displayDays[0];
    const end = displayDays[displayDays.length - 1];
    if (isSameDay(start, end)) {
      return format(start, "dd/MM/yyyy", { locale: ptBR });
    }
    return `${format(start, 'dd/MM/yyyy', { locale: ptBR })} a ${format(end, 'dd/MM/yyyy', { locale: ptBR })}`;
  }, [displayDays]);

  // Export to ICS
  const handleExportICS = async () => {
    if (exportableSchedules.length === 0) {
      toast.error('Nenhuma escala sua neste período para exportar.');
      return;
    }

    setExporting(true);
    try {
      if (nativeIOS) {
        const nativeEvents = buildNativeCalendarEventsFromSchedules(exportableSchedules).map((event, index) => ({
          ...event,
          notes: event.notes || `${scheduleTeamLabel[exportableSchedules[index]?.type] || 'Equipe'} - MBV`,
        }));
        const imported = await importEventsToNativeCalendar(nativeEvents);

        if (imported) {
          toast.success('Escalas adicionadas na agenda do iPhone.');
          setExporting(false);
          return;
        }
      }

      await downloadICSFile(
        buildICSFromSchedules(exportableSchedules),
        'escalas-integradas.ics'
      );
      
      toast.success(nativeIOS ? 'Arquivo de calendário exportado com sucesso!' : 'Calendário exportado com sucesso!');
	    } catch (error) {
	      console.error(error);
	      toast.error(
	        nativeIOS
	          ? (error?.message || 'Erro ao adicionar na agenda do iPhone')
	          : 'Erro ao exportar calendário'
	      );
	    }
    setExporting(false);
  };

  // Sync to Google Calendar
  const handleSyncGoogle = async () => {
    setExporting(true);
    try {
      let successCount = 0;
      for (const schedule of allSchedules) {
        try {
          await base44.functions.invoke('syncToGoogleCalendar', { 
            schedule: schedule
          });
          successCount++;
        } catch (err) {
          console.error('Erro ao sincronizar escala:', schedule.id, err);
        }
      }
      toast.success(`${successCount} escalas sincronizadas com Google Calendar!`);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao sincronizar com Google Calendar');
    }
    setExporting(false);
  };

  const handlePreviewPDF = async () => {
    if (visibleSchedules.length === 0) {
      toast.error('Nenhuma escala no periodo exibido para visualizar em PDF.');
      return;
    }

    setExporting(true);
    try {
      const doc = buildPdfForSchedules(visibleSchedules, periodLabel);
      const safePeriod = periodLabel.replace(/[^\d/]+/g, '-').replace(/[\/\s]+/g, '-');
      const filename = `escalas-integradas-${safePeriod}.pdf`;
      const pdfBlob = doc.output('blob');

      if (await openBlobFileOnNative(pdfBlob, filename, 'application/pdf', {
        title: 'Calendario Integrado',
        text: `PDF das escalas do periodo ${periodLabel}`,
      })) {
        toast.success('PDF aberto no dispositivo.');
      } else {
        const blobUrl = doc.output('bloburl');
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
        toast.success('Pre-visualizacao do PDF aberta em nova aba.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao gerar PDF para visualizacao.');
    }
    setExporting(false);
  };

  const handleDownloadPDF = async () => {
    if (visibleSchedules.length === 0) {
      toast.error('Nenhuma escala no periodo exibido para baixar em PDF.');
      return;
    }

    setExporting(true);
    try {
      const doc = buildPdfForSchedules(visibleSchedules, periodLabel);
      const safePeriod = periodLabel.replace(/[^\d/]+/g, '-').replace(/[\/\s]+/g, '-');
      const filename = `escalas-integradas-${safePeriod}.pdf`;
      const pdfBlob = doc.output('blob');

      if (await openBlobFileOnNative(pdfBlob, filename, 'application/pdf', {
        title: 'Calendario Integrado',
        text: `PDF das escalas do periodo ${periodLabel}`,
      })) {
        toast.success('PDF pronto para abrir/compartilhar.');
      } else {
        doc.save(filename);
        toast.success('PDF gerado com sucesso!');
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao gerar PDF.');
    }
    setExporting(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <PageHeader
          title="Calendário Integrado"
          subtitle="Escalas de Louvor e Mídia"
          showBack
          className={nativeIOS ? 'px-4 pt-3 mb-4 items-center' : ''}
          titleClassName={nativeIOS ? 'text-lg' : ''}
          subtitleClassName={nativeIOS ? 'text-xs pr-2' : ''}
        />
        <LoadingSpinner />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <PageHeader
          title="Calendário Integrado"
          showBack
          className={nativeIOS ? 'px-4 pt-3 mb-4 items-center' : ''}
          titleClassName={nativeIOS ? 'text-lg' : ''}
        />
        <div className="max-w-lg mx-auto px-4 py-12">
          <div className="bg-white rounded-xl p-8 shadow-sm text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Acesso Restrito</h3>
            <p className="text-gray-600">
              Apenas integrantes das equipes de Louvor, Mídia, Recepção ou Kids MBV podem acessar o calendário integrado.
            </p>
          </div>
        </div>
      </div>
    );
  }

    return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pb-20">
      <div className="px-4 pt-3 sm:px-0 sm:pt-0">
        <PageHeader
          title={nativeIOS ? 'Calendário' : 'Calendário Integrado'}
          subtitle={nativeIOS ? 'Suas escalas em um só lugar' : 'Todas as escalas em um só lugar'}
          showBack
          className={nativeIOS ? 'mb-4 items-center rounded-3xl bg-[#343136] px-3 py-3 shadow-sm' : ''}
          titleClassName={nativeIOS ? 'text-[1.15rem]' : ''}
          subtitleClassName={nativeIOS ? 'text-xs max-w-[220px]' : ''}
        />
      </div>
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Controls */}
        <div className={`bg-white shadow-lg mb-6 ${nativeIOS ? 'rounded-[28px] p-3' : 'rounded-2xl p-3 sm:p-4'}`}>
          <div className="flex flex-col gap-4">
            {/* View Mode Selector */}
            <div className={`grid grid-cols-3 gap-2 ${nativeIOS ? '' : 'sm:flex'}`}>
              <Button
                size="sm"
                variant={viewMode === 'month' ? 'default' : 'outline'}
                aria-pressed={viewMode === 'month'}
                onClick={() => setViewMode('month')}
                className={`mbv-calendar-view-button w-full ${nativeIOS ? 'h-11 rounded-2xl text-sm' : ''}`}
              >
                Mês
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'week' ? 'default' : 'outline'}
                aria-pressed={viewMode === 'week'}
                onClick={() => setViewMode('week')}
                className={`mbv-calendar-view-button w-full ${nativeIOS ? 'h-11 rounded-2xl text-sm' : ''}`}
              >
                Semana
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'day' ? 'default' : 'outline'}
                aria-pressed={viewMode === 'day'}
                onClick={() => setViewMode('day')}
                className={`mbv-calendar-view-button w-full ${nativeIOS ? 'h-11 rounded-2xl text-sm' : ''}`}
              >
                Dia
              </Button>
            </div>

            {/* Date Navigation */}
            <div className={`flex items-center justify-between gap-3 rounded-2xl px-2 py-2 ${nativeIOS ? 'bg-slate-100' : 'bg-slate-50 sm:bg-transparent sm:px-0 sm:py-0'}`}>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => navigate('prev')}
                className={nativeIOS ? 'h-10 w-10 rounded-full bg-white shadow-sm' : ''}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="min-w-0 flex-1 text-center px-2">
                <p className={`font-bold text-gray-900 capitalize leading-tight ${nativeIOS ? 'text-[15px]' : 'text-sm sm:text-base'}`}>
                  {viewMode === 'day' 
                    ? format(currentDate, "d 'de' MMMM", { locale: ptBR })
                    : format(currentDate, 'MMMM yyyy', { locale: ptBR })}
                </p>
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => navigate('next')}
                className={nativeIOS ? 'h-10 w-10 rounded-full bg-white shadow-sm' : ''}
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Export Options */}
            <div className={`grid grid-cols-2 gap-2 ${nativeIOS ? '' : 'sm:flex'}`}>
              <Button
                size="sm"
                variant="outline"
                onClick={handlePreviewPDF}
                disabled={exporting}
                className={`gap-2 w-full justify-center ${nativeIOS ? 'min-h-[52px] rounded-2xl text-xs leading-tight px-3' : ''}`}
              >
                <Eye className="w-4 h-4" />
                {nativeIOS ? 'Ver PDF' : 'PDF'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownloadPDF}
                disabled={exporting}
                className={`gap-2 w-full justify-center ${nativeIOS ? 'min-h-[52px] rounded-2xl text-xs leading-tight px-3' : ''}`}
              >
                <FileText className="w-4 h-4" />
                Baixar PDF
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleExportICS}
                disabled={exporting}
                className={`gap-2 w-full justify-center ${nativeIOS ? 'min-h-[52px] rounded-2xl text-xs leading-tight px-3' : ''}`}
              >
                <Download className="w-4 h-4" />
                {nativeIOS ? 'Adicionar Agenda' : '.ICS'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSyncGoogle}
                disabled={exporting}
                className={`gap-2 w-full justify-center ${nativeIOS ? 'min-h-[52px] rounded-2xl text-xs leading-tight px-3' : ''}`}
              >
                <Calendar className="w-4 h-4" />
                Google
              </Button>
            </div>
          </div>

          {/* Legend */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mt-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded"></div>
              <span className="text-sm text-gray-600">Equipe de Louvor</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gradient-to-br from-purple-500 to-indigo-600 rounded"></div>
              <span className="text-sm text-gray-600">Equipe de Mídia</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gradient-to-br from-amber-500 to-orange-600 rounded"></div>
              <span className="text-sm text-gray-600">Equipe de Recepção</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gradient-to-br from-pink-500 to-rose-600 rounded"></div>
              <span className="text-sm text-gray-600">Kids MBV</span>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        {viewMode === 'month' && (
          <div className={`bg-white shadow-lg overflow-hidden ${nativeIOS ? 'rounded-[28px] p-2' : 'rounded-2xl p-3 sm:p-4'}`}>
            {/* Week days header */}
            <div className={`${nativeIOS ? 'overflow-x-auto px-1 pb-2' : '-mx-3 overflow-x-auto px-3 pb-1 sm:mx-0 sm:px-0'}`}>
              <div className={nativeIOS ? 'min-w-[760px]' : 'min-w-[720px]'}>
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
                    <div key={day} className="text-center text-xs font-bold text-gray-600 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Days grid */}
                <div className="grid grid-cols-7 gap-2">
                  {displayDays.map((day, index) => {
                    const daySchedules = getSchedulesForDay(day);
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const isToday = isSameDay(day, new Date());

                    return (
                      <motion.div
                        key={index}
                        data-today={isToday}
                        data-current-month={isCurrentMonth}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.01 }}
                        className={`mbv-calendar-day p-2 border-2 ${nativeIOS ? 'min-h-[124px] rounded-2xl' : 'min-h-[108px] rounded-xl'} ${
                          isToday
                            ? 'border-indigo-500 bg-indigo-50'
                            : isCurrentMonth
                            ? 'border-gray-200 bg-white'
                            : 'border-gray-100 bg-gray-50'
                        }`}
                      >
                        <div className={`text-sm font-semibold mb-2 ${
                          isToday ? 'text-indigo-600' : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                        }`}>
                          {format(day, 'd')}
                        </div>
                        <div className="space-y-1">
                          {daySchedules.map(schedule => (
                            <button
                              key={schedule.id}
                              onClick={() => setSelectedSchedule(schedule)}
                              className={`w-full text-left p-1.5 rounded-lg text-[10px] font-semibold text-white truncate ${
                                schedule.type === 'worship'
                                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                                  : schedule.type === 'media'
                                  ? 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700'
                                  : schedule.type === 'reception'
                                  ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700'
                                  : 'bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700'
                              }`}
                            >
                              {schedule.start_time && `${schedule.start_time} `}
                              {schedule.title}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Week View */}
        {viewMode === 'week' && (
          <div className="mbv-calendar-week space-y-3">
            {displayDays.map((day, index) => {
              const daySchedules = getSchedulesForDay(day);
              const isToday = isSameDay(day, new Date());

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="mbv-calendar-week-card rounded-2xl p-4"
                  data-today={isToday}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="mbv-calendar-week-date w-16 h-16 rounded-xl flex flex-col items-center justify-center">
                      <span className="text-xs font-medium">
                        {format(day, 'EEE', { locale: ptBR }).toUpperCase()}
                      </span>
                      <span className="text-2xl font-bold">{format(day, 'd')}</span>
                    </div>
                    <div>
                      <p className="font-bold capitalize">{format(day, "EEEE", { locale: ptBR })}</p>
                      <p className="text-sm text-gray-500">{isToday ? 'Hoje' : format(day, "d 'de' MMMM", { locale: ptBR })}</p>
                    </div>
                  </div>

                  {daySchedules.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">Nenhuma escala agendada</p>
                  ) : (
                    <div className="space-y-2">
                      {daySchedules.map(schedule => (
                        <button
                          key={schedule.id}
                          onClick={() => setSelectedSchedule(schedule)}
                          className="mbv-calendar-schedule-card w-full text-left p-3 rounded-xl transition-all"
                          data-team={schedule.type}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 min-w-0">
                              {schedule.type === 'worship' ? (
                                <Music className="mbv-calendar-team-icon w-5 h-5 shrink-0" />
                              ) : schedule.type === 'media' ? (
                                <Video className="mbv-calendar-team-icon w-5 h-5 shrink-0" />
                              ) : schedule.type === 'reception' ? (
                                <Users className="mbv-calendar-team-icon w-5 h-5 shrink-0" />
                              ) : (
                                <Users className="mbv-calendar-team-icon w-5 h-5 shrink-0" />
                              )}
                              <div className="min-w-0">
                                <p className="font-bold break-words">{schedule.title}</p>
                                <p className="text-xs text-gray-600">
                                  {schedule.start_time} {schedule.end_time && `- ${schedule.end_time}`}
                                </p>
                              </div>
                            </div>
                            <Badge className="mbv-calendar-count shrink-0">
                              {schedule.members?.length || 0} pessoas
                            </Badge>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Day View */}
        {viewMode === 'day' && (
          <div className={`mbv-calendar-day-panel ${nativeIOS ? 'rounded-[28px] p-4' : 'rounded-2xl p-6'}`}>
            <div className="text-center mb-6">
              <h2 className={`${nativeIOS ? 'text-xl' : 'text-2xl'} font-bold capitalize leading-tight`}>
                {format(currentDate, "EEEE, d 'de' MMMM", { locale: ptBR })}
              </h2>
            </div>

            {getSchedulesForDay(currentDate).length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Nenhuma escala agendada para este dia</p>
              </div>
            ) : (
              <div className="space-y-4">
                {getSchedulesForDay(currentDate).map(schedule => (
                  <motion.button
                    key={schedule.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => setSelectedSchedule(schedule)}
                    className="mbv-calendar-schedule-card w-full text-left p-5 rounded-2xl transition-all"
                    data-team={schedule.type}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="mbv-calendar-team-mark w-12 h-12 rounded-xl flex items-center justify-center shrink-0">
                          {schedule.type === 'worship' ? (
                            <Music className="w-6 h-6" />
                          ) : schedule.type === 'media' ? (
                            <Video className="w-6 h-6" />
                          ) : schedule.type === 'reception' ? (
                            <Users className="w-6 h-6" />
                          ) : (
                            <Users className="w-6 h-6" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xl font-bold break-words">{schedule.title}</p>
                          <Badge className="mbv-calendar-team-label mt-1">
                            {schedule.type === 'worship' ? 'Equipe de Louvor' : schedule.type === 'media' ? 'Equipe de Mídia' : schedule.type === 'reception' ? 'Equipe de Recepção' : 'Kids MBV'}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className={`gap-3 text-sm ${nativeIOS ? 'grid grid-cols-1' : 'grid grid-cols-2'}`}>
                      {schedule.start_time && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Clock className="w-4 h-4" />
                          <span>{schedule.start_time} {schedule.end_time && `- ${schedule.end_time}`}</span>
                        </div>
                      )}
                      {schedule.location && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <MapPin className="w-4 h-4" />
                          <span>{schedule.location}</span>
                        </div>
                      )}
                      {schedule.members?.length > 0 && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Users className="w-4 h-4" />
                          <span>{schedule.members.length} integrantes</span>
                        </div>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Schedule Detail Modal */}
      <AnimatePresence>
        {selectedSchedule && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setSelectedSchedule(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="mbv-integrated-schedule-modal rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-hidden"
              onClick={(event) => event.stopPropagation()}
            >
              {/* Header */}
              <div className="mbv-integrated-schedule-header p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {selectedSchedule.type === 'worship' ? (
                        <Music className="w-6 h-6 text-white" />
                      ) : selectedSchedule.type === 'media' ? (
                        <Video className="w-6 h-6 text-white" />
                      ) : selectedSchedule.type === 'reception' ? (
                        <Users className="w-6 h-6 text-white" />
                      ) : (
                        <Users className="w-6 h-6 text-white" />
                      )}
                      <Badge className="mbv-integrated-schedule-badge">
                        {selectedSchedule.type === 'worship' ? 'Equipe de Louvor' : selectedSchedule.type === 'media' ? 'Equipe de Mídia' : selectedSchedule.type === 'reception' ? 'Equipe de Recepção' : 'Kids MBV'}
                      </Badge>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{selectedSchedule.title}</h3>
                    <p className="text-white/90">
                      {format(parseScheduleDate(selectedSchedule.date), "EEEE, d 'de' MMMM", { locale: ptBR })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedSchedule(null)}
                    className="text-white/80 hover:text-white"
                    aria-label="Fechar detalhes da escala"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="mbv-integrated-schedule-content p-6 overflow-y-auto max-h-[65vh]">
                <div className="space-y-4">
                  {selectedSchedule.start_time && (
                    <div className="mbv-integrated-schedule-info flex items-center gap-3 p-3 rounded-xl">
                      <Clock className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Horário</p>
                        <p className="text-sm font-bold text-gray-900">
                          {selectedSchedule.start_time} {selectedSchedule.end_time && `- ${selectedSchedule.end_time}`}
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedSchedule.location && (
                    <div className="mbv-integrated-schedule-info flex items-center gap-3 p-3 rounded-xl">
                      <MapPin className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Local</p>
                        <p className="text-sm font-bold text-gray-900">{selectedSchedule.location}</p>
                      </div>
                    </div>
                  )}

                  {selectedSchedule.rehearsal_time && (
                    <div className="mbv-integrated-schedule-info flex items-center gap-3 p-3 rounded-xl">
                      <Clock className="w-5 h-5 text-orange-600" />
                      <div>
                        <p className="text-xs text-orange-600 font-medium">
                          {selectedSchedule.type === 'reception' ? 'Horário de Chegada' : 'Ensaio'}
                        </p>
                        <p className="text-sm font-bold text-orange-900">{selectedSchedule.rehearsal_time}</p>
                      </div>
                    </div>
                  )}

                  {selectedSchedule.members?.length > 0 && (
                    <div>
                      <p className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Equipe ({selectedSchedule.members.length})
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {selectedSchedule.members.map((member, idx) => (
                          <div
                            key={idx}
                            className="mbv-integrated-schedule-member p-3 rounded-xl"
                            data-confirmed={Boolean(member.confirmed)}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm font-bold text-gray-900">{member.name}</p>
                              {member.confirmed && <span className="mbv-integrated-schedule-confirmed">Confirmado</span>}
                            </div>
                            {member.role && (
                              <p className="text-xs text-gray-600 mt-1">{member.role}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedSchedule.songs?.length > 0 && (
                    <div>
                      <p className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                        <Music className="w-4 h-4" />
                        Hinos ({selectedSchedule.songs.length})
                      </p>
                      <div className="space-y-2">
                        {selectedSchedule.songs.map((song, idx) => (
                          <div key={idx} className="mbv-integrated-schedule-song p-3 rounded-xl">
                            <p className="text-sm font-bold text-gray-900">{song.title}</p>
                            {song.key && (
                              <p className="text-xs text-gray-600 mt-1">Tom: {song.key}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedSchedule.observations && (
                    <div className="mbv-integrated-schedule-note p-3 rounded-xl">
                      <p className="text-xs text-amber-600 font-medium mb-1">Observações</p>
                      <p className="text-sm text-gray-900">{selectedSchedule.observations}</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
